import { BoxText, PageHeaderBlackText, ShadowBox } from '@elements/Common';
import { usePrintStations } from '@hooks/print-stations';
import { StatusBadge } from '@skupreme/shared/components/shipnode';
import { cn, RadioIndicator } from '@skupreme/shared/ui';
import { useState } from 'react';
import { AppListPlaceholder } from '@components/Placeholder';
import { LoaderBar } from '@components/LoaderBar';
import { ToastMessage } from '@utility/toast';
import { ErrorMessageConstant } from '@utility/constants/common';
import ShippingLabelService from '@utility/services/shipping-label';
import { BorderButton } from '@elements/Button';
import { AppModal } from '@elements/Modal';
import type {
  ComputerProps,
  ConnecterdPrintPopupProps,
} from 'types/components/connected-printer-popup';

function Computer({ computer, printerSelected, selected }: ComputerProps) {
  return (
    <ShadowBox className="sk:mb-4">
      <div className="sk:flex sk:flex-wrap sk:items-center sk:gap-2">
        <BoxText>{computer.name}</BoxText>
        <StatusBadge status={computer.status} />
      </div>
      <ul className="sk:m-0 sk:mt-2 sk:list-none sk:divide-y sk:divide-rule sk:p-0">
        {computer.printers.map(printer => {
          const checked = `${computer.id}-${printer.id}` === selected;
          return (
            <li key={printer.id} className="sk:py-2.5">
              <div className="sk:flex sk:items-center sk:justify-between sk:gap-2">
                <label
                  className={cn(
                    'sk:m-0 sk:inline-flex sk:items-center sk:gap-2',
                    printer.online
                      ? 'sk:cursor-pointer'
                      : 'sk:cursor-not-allowed sk:opacity-50',
                  )}
                >
                  <input
                    type="radio"
                    name={`printer-${computer.id}`}
                    className="sk:peer sk:sr-only"
                    disabled={!printer.online}
                    checked={checked}
                    onChange={() => printerSelected(printer.id)}
                  />
                  <RadioIndicator
                    isSelected={checked}
                    className="sk:peer-focus-visible:outline-2 sk:peer-focus-visible:outline-solid sk:peer-focus-visible:outline-offset-1 sk:peer-focus-visible:outline-primary"
                  />
                  {printer.name}
                </label>
                <StatusBadge status={printer.status} />
              </div>
            </li>
          );
        })}
      </ul>
    </ShadowBox>
  );
}

export function ConnecterdPrintPopup({
  open,
  closeModal,
  url,
  doneClick,
}: ConnecterdPrintPopupProps) {
  const { stations, loading } = usePrintStations(open);
  const [selected, setSelected] = useState<string>('');
  const [printing, setPrinting] = useState<boolean>(false);

  const printLabel = async (computerId: string, printerId: string) => {
    try {
      setPrinting(true);
      await ShippingLabelService.print({
        message: new Date().toString(),
        computer_id: computerId,
        printer_id: printerId,
        url,
      });

      ToastMessage.success('Printed successfully');
    } catch (error: any) {
      ToastMessage.error(error?.error || ErrorMessageConstant.defaultAPIError);
    } finally {
      setPrinting(false);
    }
  };

  const printerSelected = async (computerId: string, printerId: string) => {
    setSelected(`${computerId}-${printerId}`);
    await printLabel(computerId, printerId);
  };

  const close = () => {
    closeModal();
  };

  return (
    <AppModal
      open={open}
      closeModal={() => close()}
      size="mini"
      title="Connected Printer"
      footer={
        <div className="sk:flex sk:justify-end">
          <BorderButton type="button" size="mini" onClick={() => doneClick()}>
            Complete
          </BorderButton>
        </div>
      }
    >
      {printing && <LoaderBar content="Printing Label" />}
      {loading ? (
        <AppListPlaceholder hideImages />
      ) : stations.length > 0 ? (
        stations.map(item => (
          <Computer
            key={item.id}
            computer={item}
            printerSelected={value => printerSelected(item.id, value)}
            selected={selected}
          />
        ))
      ) : (
        <PageHeaderBlackText>No Printers Found</PageHeaderBlackText>
      )}
    </AppModal>
  );
}
